import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, TrendingUp, FileText } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import ProblemDetailModal from '../components/ProblemDetailModal';
import { fetchProblems, createProblem, updateProblem, deleteProblem } from '../utils/supabaseHelpers';
import { supabase } from '../supabaseClient';
import '../animations.css';

const Overview = () => {
  const [problems, setProblems] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProblems();
  }, []);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('🔐 Current user:', user);
      if (!user) {
        console.log('⚠️ No authenticated user found');
      }
    };
    checkAuth();
  }, []);

  const loadProblems = async () => {
    try {
      setLoading(true);
      const data = await fetchProblems();
      console.log('📊 Problems data loaded:', data);
      console.log('📊 Total problems count:', data.length);
      console.log('📊 Status values found:', [...new Set(data.map(p => p.status))]);
      console.log('📊 Priority values found:', [...new Set(data.map(p => p.priority))]);
      console.log('📊 Domain values found:', [...new Set(data.map(p => p.domain))]);
      
      // Log first problem structure for debugging
      if (data.length > 0) {
        console.log('📊 Sample problem structure:', Object.keys(data[0]));
        console.log('📊 First problem:', data[0]);
      } else {
        console.log('⚠️ No problems found in database');
      }
      
      setProblems(data);
    } catch (error) {
      console.error('❌ Error loading problems:', error);
      toast.error('Failed to load problems: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Test function to create sample data
  const createTestData = async () => {
    try {
      const testProblems = [
        {
          title: 'Test Problem 1',
          description: 'This is a test problem',
          status: 'open',
          domain: 'software',
          priority: 'high'
        },
        {
          title: 'Test Problem 2', 
          description: 'Another test problem',
          status: 'in progress',
          domain: 'mechanical',
          priority: 'medium'
        },
        {
          title: 'Test Problem 3',
          description: 'Third test problem',
          status: 'completed',
          domain: 'electronics',
          priority: 'low'
        }
      ];

      for (const problem of testProblems) {
        await createProblem(problem);
      }
      
      toast.success('Test data created!');
      loadProblems();
    } catch (error) {
      console.error('Error creating test data:', error);
      toast.error('Failed to create test data: ' + error.message);
    }
  };

  const stats = {
    total: problems.length,
    solved: problems.filter(p => {
      const status = p.status?.toLowerCase() || '';
      return status.includes('completed') || status.includes('done') || status.includes('solved');
    }).length,
    inProgress: problems.filter(p => {
      const status = p.status?.toLowerCase() || '';
      return status.includes('progress') || status.includes('working');
    }).length,
    openTasks: problems.filter(p => {
      const status = p.status?.toLowerCase() || '';
      return status.includes('new') || status.includes('open') || status.includes('discussion') || status.includes('review');
    }).length
  };

  // Status Distribution Data - Dynamic based on actual data
  const getStatusData = () => {
    console.log('📊 Getting status data for', problems.length, 'problems');
    
    // Get all unique status values from the problems
    const statusCounts = {};
    problems.forEach((problem, index) => {
      const status = problem.status;
      console.log(`📊 Problem ${index + 1} status:`, status);
      if (status) {
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      } else {
        console.log(`⚠️ Problem ${index + 1} has no status:`, problem);
      }
    });

    console.log('📊 Status counts:', statusCounts);

    // Define colors for different status types
    const getStatusColor = (status) => {
      if (!status) return '#9ca3af';
      
      const normalizedStatus = status.toLowerCase().replace(/[_\s]/g, '');
      
      if (normalizedStatus.includes('new') || normalizedStatus.includes('open')) return '#9ca3af';
      if (normalizedStatus.includes('discussion') || normalizedStatus.includes('review')) return '#3b82f6';
      if (normalizedStatus.includes('decided') || normalizedStatus.includes('approved')) return '#8b5cf6';
      if (normalizedStatus.includes('progress') || normalizedStatus.includes('working')) return '#60a5fa';
      if (normalizedStatus.includes('completed') || normalizedStatus.includes('done') || normalizedStatus.includes('solved')) return '#10b981';
      if (normalizedStatus.includes('blocked') || normalizedStatus.includes('stuck')) return '#ef4444';
      
      // Default colors for unknown statuses
      const colors = ['#f59e0b', '#06b6d4', '#8b5cf6', '#ec4899', '#84cc16'];
      return colors[Object.keys(statusCounts).indexOf(status) % colors.length];
    };

    // Format status name for display
    const formatStatusName = (status) => {
      if (!status) return 'No Status';
      return status.split(/[_\s]/).map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    };

    const statusData = Object.entries(statusCounts).map(([status, count]) => ({
      name: formatStatusName(status),
      value: count,
      color: getStatusColor(status),
      originalStatus: status
    }));

    console.log('📊 Final status distribution data:', statusData);
    return statusData;
  };

  const statusData = getStatusData();

  // Priority Breakdown Data
  const priorityData = [
    { name: 'Critical', value: problems.filter(p => p.priority === 'critical').length, color: '#ef4444' },
    { name: 'High', value: problems.filter(p => p.priority === 'high').length, color: '#fb923c' },
    { name: 'Medium', value: problems.filter(p => p.priority === 'medium').length, color: '#fbbf24' },
    { name: 'Low', value: problems.filter(p => p.priority === 'low').length, color: '#9ca3af' }
  ];

  // Domain Distribution
  const domainStats = [
    { domain: 'Mechanical', count: problems.filter(p => p.domain === 'mechanical').length },
    { domain: 'Software', count: problems.filter(p => p.domain === 'software').length },
    { domain: 'Electronics', count: problems.filter(p => p.domain === 'electronics').length },
    { domain: 'Management', count: problems.filter(p => p.domain === 'management').length }
  ];

  // Recent Activity (6 most recent) - Sort by updated_at, fallback to created_at
  const recentProblems = [...problems]
    .sort((a, b) => {
      const aTime = new Date(a.updated_at || a.created_at).getTime();
      const bTime = new Date(b.updated_at || b.created_at).getTime();
      return bTime - aTime;
    })
    .slice(0, 6);

  // 30-Day Trend
  const getLast30Days = () => {
    const days = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      days.push(date.toISOString().split('T')[0]);
    }
    return days;
  };

  const trendData = getLast30Days().map(date => {
    const created = problems.filter(p => {
      // Handle different date field names and formats
      const reportDate = p.dateReported || p.date_reported || p.created_at?.split('T')[0];
      return reportDate === date;
    }).length;
    const solved = problems.filter(p => {
      const reportDate = p.dateReported || p.date_reported || p.created_at?.split('T')[0];
      const status = p.status?.toLowerCase() || '';
      return (status.includes('completed') || status.includes('done') || status.includes('solved')) && reportDate === date;
    }).length;
    return {
      date: date.slice(5), // MM-DD format
      Created: created,
      Solved: solved
    };
  });

  const getDomainColor = (domain) => {
    const colors = {
      mechanical: 'badge-blue',
      software: 'badge-purple',
      electronics: 'badge-green',
      management: 'badge-yellow'
    };
    return colors[domain] || 'badge-blue';
  };

  const getStatusColor = (status) => {
    if (!status) return 'badge-blue';
    
    const normalizedStatus = status.toLowerCase().replace(/[_\s]/g, '');
    
    if (normalizedStatus.includes('new') || normalizedStatus.includes('open')) return 'badge-blue';
    if (normalizedStatus.includes('discussion') || normalizedStatus.includes('review')) return 'badge-yellow';
    if (normalizedStatus.includes('decided') || normalizedStatus.includes('approved')) return 'badge-purple';
    if (normalizedStatus.includes('progress') || normalizedStatus.includes('working')) return 'badge-orange';
    if (normalizedStatus.includes('completed') || normalizedStatus.includes('done') || normalizedStatus.includes('solved')) return 'badge-green';
    if (normalizedStatus.includes('blocked') || normalizedStatus.includes('stuck')) return 'badge-red';
    
    return 'badge-blue'; // Default
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.split(/[_\s]/).map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading overview...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="overview-page">
      {/* Analytics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="glass-card p-6" data-testid="stat-total">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">Total Tasks</div>
            <TrendingUp className="text-blue-400" size={24} />
          </div>
          <div className="text-3xl font-bold">{stats.total}</div>
        </div>

        <div className="glass-card p-6" data-testid="stat-solved">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">Solved</div>
            <CheckCircle className="text-green-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-green-400">{stats.solved}</div>
        </div>

        <div className="glass-card p-6" data-testid="stat-in-progress">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">In Progress</div>
            <Clock className="text-blue-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-blue-400">{stats.inProgress}</div>
        </div>

        <div className="glass-card p-6" data-testid="stat-open-tasks">
          <div className="flex items-center justify-between mb-4">
            <div className="text-gray-400 text-sm font-medium">Open Tasks</div>
            <FileText className="text-yellow-400" size={24} />
          </div>
          <div className="text-3xl font-bold text-yellow-400">{stats.openTasks}</div>
        </div>
      </div>

      {/* 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Status Distribution Pie Chart */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Status Distribution</h3>
            {problems.length > 0 ? (
              statusData.length > 0 ? (
                <div className="pie-chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        animationBegin={0}
                        animationDuration={800}
                        animationEasing="ease-out"
                      >
                        {statusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} className="pie-cell" />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400 mb-2">No status data found</p>
                  <p className="text-gray-500 text-sm">Problems exist but no status information available</p>
                </div>
              )
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-400 mb-2">No problems found</p>
                <p className="text-gray-500 text-sm">Create your first problem to see status distribution</p>
              </div>
            )}
          </div>

          {/* Priority Breakdown Bar Chart */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Priority Breakdown</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={priorityData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis type="number" stroke="#9ca3af" />
                <YAxis dataKey="name" type="category" stroke="#9ca3af" />
                <Tooltip contentStyle={{ backgroundColor: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)' }} />
                <Bar dataKey="value" radius={[0, 8, 8, 0]} animationBegin={0} animationDuration={1000}>
                  {priorityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} className="bar-cell" style={{ animationDelay: `${index * 100}ms` }} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Recent Activity */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {recentProblems.map((problem) => (
                <div
                  key={problem.id}
                  onClick={() => {
                    setSelectedProblem(problem);
                    setShowDetailModal(true);
                  }}
                  className="p-4 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                  data-testid={`recent-problem-${problem.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-sm line-clamp-1 flex-1">{problem.title.length > 50 ? problem.title.substring(0, 50) + '...' : problem.title}</h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${getDomainColor(problem.domain)} text-xs`}>
                      {problem.domain.charAt(0).toUpperCase() + problem.domain.slice(1)}
                    </span>
                    <span className={`badge ${getStatusColor(problem.status)} text-xs`}>
                      {formatStatus(problem.status)}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto">
                      {new Date(problem.updated_at || problem.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
              {recentProblems.length === 0 && (
                <p className="text-gray-400 text-center py-8">No recent activity</p>
              )}
            </div>
          </div>

          {/* Domain Distribution */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold mb-4">Domain Distribution</h3>
            <div className="grid grid-cols-2 gap-4">
              {domainStats.map((item, index) => (
                <div key={index} className="p-4 rounded-lg bg-white/5">
                  <div className="text-2xl font-bold mb-1">{item.count}</div>
                  <div className="text-sm text-gray-400">{item.domain}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {problems.length > 0 ? Math.round((item.count / problems.length) * 100) : 0}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 30-Day Trend Line Chart */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">30-Day Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis dataKey="date" stroke="#9ca3af" />
            <YAxis stroke="#9ca3af" />
            <Tooltip contentStyle={{ backgroundColor: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)' }} />
            <Legend />
            <Line type="monotone" dataKey="Created" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} animationDuration={1000} />
            <Line type="monotone" dataKey="Solved" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} animationDuration={1000} animationBegin={200} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedProblem && (
        <ProblemDetailModal
          problem={selectedProblem}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedProblem(null);
          }}
          onEdit={() => {}}
          onDelete={async () => {
            try {
              await deleteProblem(selectedProblem.id);
              setProblems(problems.filter(p => p.id !== selectedProblem.id));
              toast.success('Problem deleted successfully');
              setShowDetailModal(false);
              setSelectedProblem(null);
            } catch (error) {
              toast.error('Failed to delete: ' + error.message);
            }
          }}
          onUpdate={async (updatedProblem) => {
            try {
              const updated = await updateProblem(updatedProblem.id, {
                status: updatedProblem.status
              });
              setProblems(problems.map(p => p.id === updated.id ? updated : p));
              setSelectedProblem(updated);
              toast.success('Status updated');
            } catch (error) {
              toast.error('Failed to update: ' + error.message);
            }
          }}
        />
      )}
    </div>
  );
};

export default Overview;
